// Words for the audit trail's three machine columns (spec 16 §2.16, §4).
//
// Every audit row carries an `action` (`<context>.<thing_happened>`), an
// `entity_type` and, for a person, a user id. The screen printed all three
// verbatim, so a plant manager read `quality.hold_released`, `qc_hold` and a
// uuid chip where the trail should say "Hold released", "Quality hold" and a
// name. This file turns the first two into words in the reader's language.
//
// THREE TIERS, because about 250 actions are written today, some are built at
// runtime (module 04 passes its own), and new modules add more:
//   1. an exact label (`audit.action.<context>.<thing>`) for the actions a
//      factory reads most;
//   2. otherwise the area and the verb the action ends in ("Sales: sent"),
//      from two short vocabularies that cover the rest;
//   3. otherwise the area and "other change".
// No tier prints the code. The code itself stays on the cell's title, where a
// support conversation can still quote it.
//
// Entity types follow the same rule: a label for each known type, and
// "Record" for one this build does not know, with the type on the title.

import type { i18n as I18n, TFunction } from "i18next";
import type { RefKind } from "../../../../lib/ref-names";

/** The trail's areas: the filter's nine plus the modules that joined later. */
export const AUDIT_LABEL_AREAS = [
  "identity",
  "catalogue",
  "onboarding",
  "inventory",
  "production",
  "quality",
  "waste",
  "files",
  "agent",
  "sales",
  "messaging",
  "planning",
  "other",
] as const;
export type AuditLabelArea = (typeof AUDIT_LABEL_AREAS)[number];

const PLANNING_CONTEXTS = new Set([
  "planning",
  "mps_plan",
  "mrp_run",
  "planned_order",
  "schedule",
  "schedule_directive",
  "schedule_overload",
  "setup_matrix",
  "forecast",
  "demand_refresh",
]);

/** The area an action's context belongs to. */
export function auditAreaOf(context: string): AuditLabelArea {
  if (context === "sales" || context.startsWith("sales_")) return "sales";
  if (PLANNING_CONTEXTS.has(context) || context.startsWith("planning_")) return "planning";
  if (context === "staff" || context === "tenant" || context === "impersonation") return "identity";
  return (AUDIT_LABEL_AREAS as readonly string[]).includes(context)
    ? (context as AuditLabelArea)
    : "other";
}

/**
 * The verbs an action may end in, each with a label in every locale. A
 * two-word verb (`opted_in`) is matched before its last word.
 */
export const AUDIT_VERBS = [
  "accepted",
  "activated",
  "added",
  "adjusted",
  "applied",
  "approved",
  "archived",
  "attached",
  "cancelled",
  "changed",
  "closed",
  "completed",
  "confirmed",
  "connected",
  "converted",
  "created",
  "deactivated",
  "decided",
  "declined",
  "deleted",
  "delivered",
  "detached",
  "disabled",
  "discarded",
  "disconnected",
  "dispatched",
  "enabled",
  "ended",
  "expired",
  "failed",
  "firmed",
  "flagged",
  "generated",
  "imported",
  "installed",
  "invited",
  "issued",
  "linked",
  "logged",
  "lost",
  "merged",
  "opened",
  "packed",
  "picked",
  "placed",
  "posted",
  "proposed",
  "published",
  "quarantined",
  "received",
  "recorded",
  "registered",
  "rejected",
  "released",
  "removed",
  "renamed",
  "reopened",
  "replaced",
  "reported",
  "requested",
  "resolved",
  "restored",
  "reversed",
  "revised",
  "revoked",
  "saved",
  "sent",
  "shipped",
  "started",
  "submitted",
  "updated",
  "upserted",
  "verified",
  "waived",
  "withdrawn",
  "won",
] as const;

/** The actions a factory reads most, each with its own label. */
export const AUDIT_EXACT_ACTIONS = [
  "catalogue.product_created",
  "catalogue.product_updated",
  "catalogue.product_activated",
  "catalogue.bom_activated",
  "catalogue.routing_activated",
  "catalogue.cost_run_released",
  "identity.invite_created",
  "identity.invite_accepted",
  "identity.member_provisioned",
  "identity.member_role_changed",
  "identity.member_revoked",
  "identity.facility_created",
  "identity.work_center_created",
  "onboarding.went_live",
  "onboarding.import_committed",
  "onboarding.pack_installed",
  "onboarding.step_completed",
  "onboarding.checklist_item_waived",
  "inventory.movement_recorded",
  "inventory.count_posted",
  "inventory.transfer_dispatched",
  "inventory.transfer_received",
  "inventory.lot_quarantined",
  "inventory.lot_released",
  "inventory.adjustment_request_decided",
  "production.work_order_created",
  "production.work_order_released",
  "production.work_order_completed",
  "production.work_order_closed",
  "production.operation_started",
  "production.operation_completed",
  "production.qty_reported",
  "production.downtime_logged",
  "production.shift_closed",
  "production.cost_adjusted",
  "quality.inspection_completed",
  "quality.inspection_result_recorded",
  "quality.hold_placed",
  "quality.hold_released",
  "waste.event_recorded",
  "waste.remnant_registered",
  "sales.customer_created",
  "sales.customer_updated",
  "sales_quote.created",
  "sales_quote.sent",
  "sales_quote.revised",
  "sales_order.created",
  "sales_order.cancelled",
  "sales_shipment.shipped",
  "sales_opportunity.won",
  "sales_opportunity.lost",
  "planned_order.firmed",
  "planned_order.converted",
  "schedule.published",
  "mps_plan.approved",
] as const;

/** Entity types with a label. Plural spellings two writers use fold onto these. */
export const AUDIT_ENTITY_TYPES = [
  "tenant",
  "user",
  "facility",
  "department",
  "work_center",
  "product",
  "bom",
  "routing",
  "catalogue_template",
  "uom",
  "cost_run",
  "attribute_schema",
  "import_job",
  "onboarding_session",
  "onboarding_step_state",
  "go_live_checklist_item",
  "terminology_override",
  "vertical_pack_install",
  "qr_print_batch",
  "stock_unit",
  "lot",
  "location",
  "cycle_count",
  "adjustment_request",
  "inventory_settings",
  "work_order",
  "shift_definition",
  "downtime_log",
  "downtime_reason",
  "floor_device",
  "qc_inspection",
  "qc_hold",
  "qc_control_point",
  "qc_failure_cause",
  "qc_cut_event",
  "waste_event",
  "waste_category",
  "waste_cause_code",
  "remnant",
  "remnant_price_class",
  "waste_settings",
  "customer",
  "customer_contact",
  "contact_channel",
  "quote",
  "sales_order",
  "sales_shipment",
  "sales_opportunity",
  "sales_activity",
  "price_list",
  "planned_order",
  "mps_plan",
  "schedule",
  "mrp_run",
  "forecast",
  "conversation",
  "message",
  "message_template",
  "file",
] as const;

const ENTITY_ALIASES: Readonly<Record<string, string>> = {
  waste_events: "waste_event",
  remnants: "remnant",
  contact_channels: "contact_channel",
  sales_quote: "quote",
};

/** Entity types whose id the names read can turn into a name (spec 16 §3.13). */
const ENTITY_REF_KINDS: Readonly<Record<string, RefKind>> = {
  user: "user",
  facility: "facility",
  work_center: "work_center",
  product: "product",
  stock_unit: "stock_unit",
  lot: "lot",
  location: "location",
  customer: "customer",
};

export function auditEntityRefKind(entityType: string): RefKind | undefined {
  return ENTITY_REF_KINDS[ENTITY_ALIASES[entityType] ?? entityType];
}

type Translate = Pick<I18n, "exists"> & { t: TFunction };

function areaLabel(tr: Translate, area: AuditLabelArea): string {
  return tr.t(`audit.area.${area}`);
}

/** "What happened", in the reader's language. Never the code. */
export function auditActionLabel(tr: Translate, action: string): string {
  const dot = action.indexOf(".");
  const context = dot < 0 ? action : action.slice(0, dot);
  const thing = dot < 0 ? "" : action.slice(dot + 1);
  const exact = `audit.action.${context}.${thing}`;
  if (thing.length > 0 && tr.exists(exact)) return tr.t(exact);

  const area = areaLabel(tr, auditAreaOf(context));
  const words = (thing.length > 0 ? thing : context).split("_");
  for (let take = Math.min(2, words.length); take >= 1; take -= 1) {
    const verb = words.slice(-take).join("_");
    if ((AUDIT_VERBS as readonly string[]).includes(verb)) {
      return tr.t("audit.actionPhrase", { area, verb: tr.t(`audit.verb.${verb}`) });
    }
  }
  return tr.t("audit.actionOther", { area });
}

/** The kind of record a row is about, in the reader's language. */
export function auditEntityLabel(tr: Translate, entityType: string): string {
  const type = ENTITY_ALIASES[entityType] ?? entityType;
  return (AUDIT_ENTITY_TYPES as readonly string[]).includes(type)
    ? tr.t(`audit.entity.${type}`)
    : tr.t("audit.entityOther");
}
